import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DroppableProvided, DraggableProvided, DropResult } from 'react-beautiful-dnd';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NoPostsCard } from '../common/NoPostsCard';
import { normalizeMedia } from '../../utils/mediaUtils';
import type { Post } from '../../types/calendar';
import { MediaDisplay } from '../posts/previews/MediaDisplay';
import { PdfThumb } from '../common/PdfThumb';

interface BoardViewProps {
  posts: Post[];
  onPostClick: (post: Post) => void;
}

export const BoardView: React.FC<BoardViewProps> = ({ posts, onPostClick }) => {
  const navigate = useNavigate();
  
  // Group posts by status
  const grouped = posts.reduce<Record<string, Post[]>>((acc, post) => {
    const status = post.status || 'draft';
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(post);
    return acc;
  }, {});
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'waiting':
        return 'bg-orange-100 text-orange-800';
      case 'processing':
        return 'bg-purple-100 text-purple-800';
      case 'published':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'draft':
        return '📝';
      case 'scheduled':
        return '📅';
      case 'pending':
        return '🚧';
      case 'waiting':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'published':
        return '✅';
      default:
        return '';
    }
  };
  
  // Get status name in sentence case
  const getStatusName = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };
  
  // Order the columns: draft, scheduled, pending, waiting, processing, published
  const statusOrder = ['draft', 'scheduled', 'pending', 'waiting', 'processing', 'published'];
  
  // Add state for drag and drop
  const [columns, setColumns] = useState(grouped);
  
  // Handle drag and drop
  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    
    // Drop outside a droppable area
    if (!destination) return;
    
    // Drop in the same place
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }
    
    // Find the post
    const post = posts.find(p => p.id === draggableId);
    if (!post) return;
    
    // Create new columns
    const newColumns = { ...columns };
    
    // Remove from source
    const sourceColumn = [...(newColumns[source.droppableId] || [])];
    sourceColumn.splice(source.index, 1);
    newColumns[source.droppableId] = sourceColumn;
    
    // Add to destination
    const destColumn = [...(newColumns[destination.droppableId] || [])];
    destColumn.splice(destination.index, 0, post);
    newColumns[destination.droppableId] = destColumn;
    
    // Update state
    setColumns(newColumns);
    
    // This would be where you would make an API call to update the post status
    console.log(`Moving post ${draggableId} from ${source.droppableId} to ${destination.droppableId}`);
  };
  
  if (posts.length === 0) {
    return (
      <NoPostsCard 
        title="Your content board is empty"
        message="Create your first post to see it displayed on this board. You'll be able to track your content's status from draft to published!"
        icon="board"
        isFiltered={false}
      />
    );
  }

  return (
    <div className="overflow-x-auto pb-4">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-flow-col auto-cols-[minmax(300px,1fr)] gap-4 min-w-full">
          {statusOrder.map(status => (
            <Droppable key={status} droppableId={status}>
              {(provided: DroppableProvided) => (
                <div 
                  className="bg-white rounded-xl border border-black shadow-lg overflow-hidden h-[calc(100vh-280px)]"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  <div className={`p-3 ${getStatusColor(status)} flex items-center justify-between sticky top-0`}>
                    <div className="flex items-center">
                      <span className="mr-2">{getStatusEmoji(status)}</span>
                      <span className="font-medium">{getStatusName(status)}</span>
                    </div>
                    <span className="text-sm bg-white bg-opacity-80 px-2 py-0.5 rounded-full">
                      {columns[status]?.length || 0}
                    </span>
                  </div>
                  <div className="p-2 overflow-y-auto h-[calc(100%-56px)]">
                    {columns[status]?.map((post, index) => (
                      <Draggable 
                        key={post.id} 
                        draggableId={post.id} 
                        index={index}
                      >
                        {(provided: DraggableProvided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="mb-2 p-3 bg-white rounded-lg border border-gray-200 shadow-sm hover:border-primary-300 cursor-pointer"
                            onClick={() => onPostClick(post)}
                          >
                            <div className="flex items-start gap-2 mb-2">
                              {(() => {
                                const normalizedMedia = normalizeMedia(post.media);
                                if (!normalizedMedia) return null;
                                
                                const isPdf = /\.pdf(\?|#|$)/i.test(normalizedMedia.url);
                                
                                return (
                                  <div className="w-10 h-10 rounded bg-gray-100 flex-shrink-0 overflow-hidden">
                                    {isPdf ? (
                                      <PdfThumb 
                                        src={normalizedMedia.url} 
                                        className="w-full h-full" 
                                        frame="square" 
                                      />
                                    ) : (
                                      <MediaDisplay
                                        mediaUrl={normalizedMedia.url}
                                        altText="Post media"
                                        type={normalizedMedia.type}
                                        className="w-full h-full"
                                        objectFit="cover"
                                        controls={false}
                                        autoPlayOnMount={false}
                                        muted={true}
                                      />
                                    )}
                                  </div>
                                );
                              })()}
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-900 line-clamp-2">
                                  {post.content}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex gap-1">
                                {post.platforms.map((platform) => (
                                  <span key={platform} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">
                                    {platform}
                                  </span>
                                ))}
                              </div>
                              {post.brandName && (
                                <span className="text-xs text-gray-500">
                                  {post.brandName}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {(!columns[status] || columns[status].length === 0) && (
                      <div className="p-4 text-center text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg mt-2">
                        <p>Drop posts here</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
};